import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import CTAButton from "@/components/CTAButton";
import { Reveal, Stagger, StaggerItem } from "@/components/motion";
import { SectionHeading } from "@/components/primitives";
import Icon from "@/components/Icon";
import ModuleSpotlight from "@/components/sections/ModuleSpotlight";
import PwaSection from "@/components/sections/PwaSection";
import FinalCTA from "@/components/sections/FinalCTA";
import { ArrowRight } from "lucide-react";

const LAYERS = [
  {
    step: "01",
    kicker: "Understand",
    title: "See what's really happening.",
    desc: "Business Briefing and Command Center bring together your priorities, pipeline, overdue work and goals — grounded in your actual business context.",
    modules: ["Business Briefing", "Business Memory", "Command Center"],
    icon: "LayoutDashboard",
  },
  {
    step: "02",
    kicker: "Decide",
    title: "Think with a leadership team.",
    desc: "Ask the Board strategy, growth, marketing, finance, operations, tech or people questions. Provide context, challenge assumptions, adopt decisions.",
    modules: ["Board · CEO, CGO, CMO, CFO, COO, CTO, CHRO", "Chief of Staff", "Decision → Execution drafts"],
    icon: "Landmark",
  },
  {
    step: "03",
    kicker: "Execute",
    title: "Turn decisions into action.",
    desc: "Set goals with clear milestones, plan projects, schedule tasks with dates and reminders, and keep every commitment visible on your calendar.",
    modules: ["Goals", "Work / Tasks", "Projects", "Calendar", "Meeting Assist"],
    icon: "CheckSquare",
  },
  {
    step: "04",
    kicker: "Grow",
    title: "Keep revenue moving.",
    desc: "Companies, contacts, deals, pipeline and follow-ups all connected. Won deals draft their next-step plan automatically.",
    modules: ["CRM · Companies + Contacts + Deals", "Pipeline & Follow-ups", "Won → Delivery / Follow-up"],
    icon: "Contact",
  },
  {
    step: "05",
    kicker: "Adapt",
    title: "Track what's unique to you.",
    desc: "Business Data lets you spin up trackers for records that don't fit CRM or Projects — vendors, candidates, campaigns, inventory, assets and more.",
    modules: ["Business Data (custom trackers)", "Business Assistant · delegate, don't just ask", "Shared business context"],
    icon: "Database",
  },
];

export default function Product() {
  return (
    <>
      <Seo
        title="Product — One system for running the business"
        description="How Stupeco WorkSpace works as one business operating system: Understand, Decide, Execute, Grow, Adapt."
        path="/product"
      />
      <PageHeader
        eyebrow="Product"
        title="One system for running the business."
        subtitle="Every module shares the same business context, so briefings, decisions, plans and follow-through connect naturally — no more copy-pasting between disconnected tools."
      >
        <div className="flex flex-wrap gap-3">
          <CTAButton to="/signup" variant="gradient" size="md" data-testid="product-start-free-btn">
            Start Free <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </CTAButton>
          <CTAButton to="/features" variant="outline" size="md">See all features</CTAButton>
        </div>
      </PageHeader>

      {/* Understand → Decide → Execute → Grow → Adapt narrative */}
      <section className="border-b border-border py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="How WorkSpace works"
            title="Understand. Decide. Execute. Grow. Adapt."
            subtitle="Five connected layers — sharing one business context."
          />
          <Stagger className="mt-14 grid gap-5 lg:grid-cols-2">
            {LAYERS.map((l, i) => (
              <StaggerItem key={l.step} className={`h-full ${i === 0 ? "lg:col-span-2" : ""}`}>
                <div className="card-hover flex h-full flex-col gap-4 rounded-2xl border border-border bg-card p-6 hover:border-foreground/20">
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] font-bold tracking-[0.2em] text-muted-foreground">{l.step}</span>
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-muted/60 text-brand-teal">
                      <Icon name={l.icon} className="h-4 w-4" />
                    </span>
                    <span className="eyebrow text-brand-teal">{l.kicker}</span>
                  </div>
                  <h3 className="font-display text-xl font-semibold tracking-tight">{l.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{l.desc}</p>
                  <ul className="mt-auto flex flex-wrap gap-1.5 border-t border-border pt-4">
                    {l.modules.map((m) => (
                      <li key={m} className="rounded-full bg-muted/50 px-2 py-1 text-[11px] font-medium text-muted-foreground">{m}</li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* 2 hero screenshots supporting the narrative — Command Center + Board */}
      <ModuleSpotlight
        testid="product-cc-spotlight"
        eyebrow="Understand · Command Center"
        title="Everything that needs your attention, in one calm view."
        description="Business Briefing summarises the state of your business. Priorities, overdue items, upcoming commitments, deals needing follow-up and goal progress — all in one place, grounded in real data."
        screenshot="/product-shots/command-center-real.png"
        screenshotAlt="Command Center"
      />
      <ModuleSpotlight
        testid="product-board-spotlight"
        reverse
        accent="text-brand-blue"
        eyebrow="Decide · Board"
        title="A leadership team that already knows your business."
        description="Ask strategy questions, challenge assumptions and adopt decisions. When information is missing the Board asks for it, and when a decision is adopted the Chief of Staff drafts the execution plan."
        screenshot="/product-shots/board-real.png"
        screenshotAlt="Board · Leadership Team"
      />

      <PwaSection />
      <FinalCTA />
    </>
  );
}
